import dayjs, { Dayjs } from "dayjs";
import { FeatureCollection } from "geojson"; // TODO: We don't even have this in package.json?!?
import mapboxgl from "mapbox-gl";
import { useRef, useState } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { Area } from "../models/Area";
import { Dropoff } from "../models/Dropoff";
import { Equipment } from "../models/Equipment";
import { Gate } from "../models/Gate";
import { GateToDropoffRoute } from "../models/GateToDropoffRoute";
import { HaulRoute } from "../models/HaulRoute";
import { MapObject } from "../models/MapObject";
import { WaypointStep } from "../models/WaypointStep";

// The gate/dropoff pairing hovered in a gate's "Allowed Dropoffs" or a dropoff's "Allowed Gates" list. Kept apart from
// hoveredMapObject because the legend beside those lists sets that too, for every item it shows, and a legend hover must
// not swap out the open item's default.
export type HoveredDropoffGatePair = {
    gateId: number;
    dropoffId: number;
};

export type PendingGateToDropoffRoute = {
    gateId: number;
    dropoffId: number;
    name: string;
};

// The gate or dropoff whose editor a Gate-to-Dropoff Route was opened from, as it was at that moment (unsaved changes
// included), so saving or cancelling the route goes back to editing it.
export type GateToDropoffRouteReturnTo = {
    mappable: Gate | Dropoff;
    mapObject: MapObject;
};

const useMapInfo = () => {
    const mapRef = useRef<MapRef>(null);
    const [mapGeoJson, setMapGeoJson] = useState<FeatureCollection | null>(null);
    const [mapBoxDraw, setMapBoxDraw] = useState<any>(null);
    const mapPopup = new mapboxgl.Popup({
        maxWidth: "50%",
        closeButton: false,
        closeOnClick: false,
    });
    const [currentMappable, setCurrentMappable] = useState<
        Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute | null
    >(null);
    const [currentMapObject, setCurrentMapObject] = useState<MapObject | null>(null);
    const [editCurrentMappable, setEditCurrentMappable] = useState<boolean>(false);
    const [showOtherMapItemsWhileEditing, setShowOtherMapItemsWhileEditing] = useState<boolean>(false);
    const [showGeotiff, setShowGeotiff] = useState<boolean>(true);
    const [newMappableType, setNewMappableType] = useState<
        "dropoff" | "location" | "equipment" | "gate" | "haul_route" | "gate_to_dropoff_route" | null
    >(null);
    // Set right before entering draw mode for a new GateToDropoffRoute, since the map's draw-mode
    // state machine only otherwise knows the mappable type being drawn, not which gate/dropoff this
    // specific route belongs to.
    const [pendingGateToDropoffRoute, setPendingGateToDropoffRoute] = useState<PendingGateToDropoffRoute | null>(null);
    const [gateToDropoffRouteReturnTo, setGateToDropoffRouteReturnTo] = useState<GateToDropoffRouteReturnTo | null>(
        null
    );
    const [editCurrentMappablePosition, setEditCurrentMappablePosition] = useState<boolean>(false);
    const [settingJobsiteCoordinates, setSettingJobsiteCoordinates] = useState<boolean>(false);
    const [mapFilterDate, setMapFilterDate] = useState<Dayjs | null>(dayjs());
    const [currentWaypointStep, setCurrentWaypointStep] = useState<WaypointStep | null>(null);
    const [hoveredMapObject, setHoveredMapObject] = useState<MapObject | null>(null);
    const [hoveredDropoffGatePair, setHoveredDropoffGatePair] = useState<HoveredDropoffGatePair | null>(null);
    const mapBounds = new mapboxgl.LngLatBounds();
    const [userLocation, setUserLocation] = useState<GeolocationCoordinates | null>(null);
    const geoControlRef = useRef<mapboxgl.GeolocateControl>(null);
    const [waypointGuidance, setWaypointGuidance] = useState<boolean>(false);

    return {
        mapRef,

        mapGeoJson,
        setMapGeoJson,

        mapPopup,

        mapBoxDraw,
        setMapBoxDraw,

        currentMappable,
        setCurrentMappable,

        currentMapObject,
        setCurrentMapObject,

        editCurrentMappable,
        setEditCurrentMappable,

        showOtherMapItemsWhileEditing,
        setShowOtherMapItemsWhileEditing,

        showGeotiff,
        setShowGeotiff,

        newMappableType,
        setNewMappableType,

        pendingGateToDropoffRoute,
        setPendingGateToDropoffRoute,
        gateToDropoffRouteReturnTo,
        setGateToDropoffRouteReturnTo,

        editCurrentMappablePosition,
        setEditCurrentMappablePosition,

        settingJobsiteCoordinates,
        setSettingJobsiteCoordinates,

        mapFilterDate,
        setMapFilterDate,

        currentWaypointStep,
        setCurrentWaypointStep,

        hoveredMapObject,
        setHoveredMapObject,

        hoveredDropoffGatePair,
        setHoveredDropoffGatePair,

        mapBounds,

        userLocation,
        setUserLocation,

        geoControlRef,

        waypointGuidance,
        setWaypointGuidance,
    };
};

export default useMapInfo;
